<template>
  <site-events
    :tabs="allTabs"
    :events="currentItems()"
    :event-signal-by-id="eventSignalById"
    :pages="eventsPages"
    :count="currentCount()"
    @refresh-request="handleRefresh"
    @download-request="downloadEvents"
  />
</template>

<script>
import { get } from 'vuex-pathify'
import EventService from "@/services/event.service"
import { format, subDays, subMonths, subYears, startOfDay, endOfDay, startOfMonth, endOfMonth, parseISO, isWithinInterval } from "date-fns"
import DownloadEventsDialog from "@/components/events/dialogs/DownloadEventsDialog.vue"

export default {
  name: "Events",
  created() {
    this.$store.dispatch('dashboard/fetchOpenEvents').catch(err => {
      console.warn('Unable to refresh open events on Events view load', err)
    })
  },
  data() {
    return {
      events: [],
      eventsPages: 0,
      eventsCount: 0,
      currentTab: 'dashboard_my', // Default to first dashboard tab
      currentContext: null, // Store current filter context
    }
  },
  computed: {
    myEvents: get('dashboard/myEventsWithSignals'),
    notMyEvents: get('dashboard/notMyEventsWithSignals'),
    unassignedEvents: get('dashboard/unassignedEventsWithSignals'),
    eventSignalById: get('dashboard/eventSignalById'),

    allTabs() {
      const eventTabs = Array.isArray(EventService.getMainConditions())
        ? EventService.getMainConditions().filter(tab => tab && tab.id)
        : []
      const dashboardTabs = [
        { id: "dashboard_my", name: "Moje", params: {}, isDashboard: true, collection: this.myEvents },
        { id: "dashboard_unassigned", name: "Nieprzypisane", params: {}, isDashboard: true, collection: this.unassignedEvents },
        { id: "dashboard_other", name: "Reszta", params: {}, isDashboard: true, collection: this.notMyEvents },
      ]
      const all = [...dashboardTabs, ...eventTabs]
      const seen = new Set()
      return all.filter(tab => {
        if (!tab?.id || seen.has(tab.id)) {
          return false
        }
        seen.add(tab.id)
        return true
      })
    },
  },
  methods: {
    findTabById(tabId) {
      return this.allTabs.find(tab => tab.id === tabId)
    },

    // Filter local collection based on context (same filters as API)
    filterLocalCollection(collection, context) {
      if (!collection || !context) return collection

      let filtered = [...collection]

      // Filter by search text
      if (context.filter) {
        const searchLower = context.filter.toLowerCase()
        filtered = filtered.filter(event => {
          // Search in event fields
          const searchableText = [
            event.code.text,
            event.site?.name,
            event.site?.address,
            ...(event.tasks?.map(t => t.description) || [])
          ].filter(Boolean).join(' ').toLowerCase()

          return searchableText.includes(searchLower)
        })
      }

      // Filter by category (if has params with specific filters)
      if (context.category?.params) {
        const categoryParams = context.category.params

        // Filter by state if specified
        if (categoryParams.category) {
          filtered = filtered.filter(event => event.code.category.startsWith(categoryParams.category))
        }

        // Filter by task_type if specified
        if (categoryParams.task_type) {
          filtered = filtered.filter(event => {
            return event.tasks?.some(task => task.task_type == categoryParams.task_type)
          })
        }
      }

      if (context.range?.id) {
        const now = new Date()
        let fromDate = null
        let toDate = endOfDay(now)

        switch (context.range.id) {
          case 'today':
            fromDate = startOfDay(now)
            toDate = endOfDay(now)
            break
          case 'yesterday':
            fromDate = startOfDay(subDays(now, 1))
            toDate = endOfDay(subDays(now, 1))
            break
          case 'this_month':
            fromDate = startOfMonth(now)
            toDate = endOfMonth(now)
            break
          case 'prev_month': {
            const prevMonth = subMonths(now, 1)
            fromDate = startOfMonth(prevMonth)
            toDate = endOfMonth(prevMonth)
            break
          }
          case 'last_30_days':
            fromDate = startOfDay(subDays(now, 30))
            toDate = endOfDay(now)
            break
          case 'last_year':
            fromDate = startOfDay(subYears(now, 1))
            toDate = endOfDay(now)
            break
          case 'custom':
            if (context.range.from) fromDate = parseISO(context.range.from)
            if (context.range.to) toDate = parseISO(context.range.to)
            break
          default:
            // No date filter (e.g., 'all')
            fromDate = null
        }

        if (fromDate) {
          filtered = filtered.filter(event => {
            const eventDate = parseISO(event.created_at)
            return isWithinInterval(eventDate, { start: fromDate, end: toDate })
          })
        }
      }

      // Sort by ordering
      if (context.ordering) {
        const isDesc = context.ordering.startsWith('-')
        const field = isDesc ? context.ordering.slice(1) : context.ordering

        filtered.sort((a, b) => {
          let valA = a[field]
          let valB = b[field]

          // Handle date fields
          if (field === 'created_at' || field === 'updated_at' || field === 'modified_at') {
            valA = new Date(valA).getTime()
            valB = new Date(valB).getTime()
          }

          if (valA < valB) return isDesc ? 1 : -1
          if (valA > valB) return isDesc ? -1 : 1
          return 0
        })
      }

      return filtered
    },

    currentItems() {
      // Default to dashboard_my if no tab selected
      const activeTab = this.currentTab || 'dashboard_my'

      const collection = this.findTabById(activeTab)?.collection
      if (collection) {
        // Apply local filtering
        return this.filterLocalCollection(collection, this.currentContext)
      } else {
        return this.events
      }
    },

    currentCount() {
      const activeTab = this.currentTab || 'dashboard_my'
      const collection = this.findTabById(activeTab)?.collection

      if (collection) {
        // Return filtered count for dashboard tabs
        return this.filterLocalCollection(collection, this.currentContext).length
      }

      return this.eventsCount
    },
    handleRefresh(context) {
      // Get tab ID from context (directly or from query.state for regular tabs)
      const tabId = context.tab || context.query?.state

      console.log('handleRefresh - tabId:', tabId, 'context:', context)

      // Store context for local filtering
      this.currentContext = context

      // Update current tab immediately
      this.currentTab = tabId

      // Check if it's a dashboard tab
      const isDashboard = this.findTabById(tabId)?.isDashboard
      console.log('isDashboard :>> ', isDashboard);
      if (isDashboard) {
        // Dashboard tabs don't need API call - data from Vuex with local filtering
        return
      }

      // Regular events tab - fetch from API
      this.refreshEvents(context)
    },

    refreshEvents(context) {
      EventService.fetchEvents(
        context.query,
        context?.page,
        context?.ordering
      ).then(response => {
        if (response?.data) {
          this.eventsPages = response.data.pages
          this.eventsCount = response.data.count
          this.events = response.data.results
        }
      })
    },

    async downloadEvents(context) {
      console.log('downloadEvents :>> ', context);
      const result = await this.$dialog.show(DownloadEventsDialog, {
        title: "",
        waitForResult: true,
      });
      if (result === false || result === undefined) {
        return;
      }

      const query = {
        ...context.query,
        ...result,
      }
      console.log('result :>> ', query);
      EventService.download(
        null,
        query,
      ).then(response => {
        console.log("ok", response.data)
        const blob = new Blob([response.data], {
          type: "application/pdf;charset=utf-8;"
        })
        const link = document.createElement("a")
        link.href = URL.createObjectURL(blob)
        link.download = `raport_${format(
          new Date(),
          "yyyMMddHHmmss"
        )}.pdf`
        link.click()
        URL.revokeObjectURL(link.href)
      })
    }
  }
}
</script>
